import { createAsyncThunk } from '@reduxjs/toolkit';
import { baseApi } from 'shared/api/base-api';
import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import { addPlayerToTeam, removePlayerFromTeam } from 'modules/teams/store/teams';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { buildTournamentApplicationUserDto } from 'shared/utils/build-tournament-application-user-dto';
import { ChangeParticipantsRoleDto } from 'modules/teams/domain/dtos/change-participants-role';
import { KickFromTeamInputDto } from 'modules/teams/domain/dtos/kick-from-team-input.dto';
import { UpsertTournamentPlayerApplicationDto } from 'modules/teams/domain/dtos/upsert-tournament-player-action.dto';
import { Player } from 'modules/players/domain/interfaces/player';
import { UpsertTournamentApplicationUserDto } from 'modules/players/domain/interfaces/upsert-player-input.dto';
import playersRepository from 'modules/players/domain/repositories/player.repository';
import playersResource from 'modules/players/domain/resources/players-resource';
import teamParticipantsResource from 'modules/teams/domain/resources/team-participants-resource';

const PREFIX = 'teams/players';

/**
 * Мутации в проекте остаются thunk'ами, поэтому кэш RTK Query они сбрасывают
 * сами — иначе карточка команды покажет состав до изменения.
 */
const invalidateTeam = (teamId: number) => baseApi.util.invalidateTags([{ type: 'Team' as const, id: teamId }]);

export const upsertTournamentPlayerApplication = createAsyncThunk<Player, UpsertTournamentPlayerApplicationDto>(
  `${PREFIX}/upsertTournamentPlayerApplication`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { tournamentId, userId, photo, teamId } = payload;
      let player: UpsertTournamentApplicationUserDto = buildTournamentApplicationUserDto(payload);

      let playerId: number | null = null;

      try {
        let result = await playersRepository.upsertTournamentApplicationForUser(player, tournamentId);
        playerId = result.id;
      } catch (err) {
        if (err === 'User is already in this team') {
          console.warn(`${PREFIX}/upsertTournamentPlayerApplication warning:`, err);
        } else {
          console.error(`${PREFIX}/upsertTournamentPlayerApplication error:`, err);
        }

        if (userId) {
          playerId = userId;
        }
      }

      if (!playerId) {
        throw new Error('Player ID not found');
      }

      // `instanceof File` — форма может отдать уже сохранённое фото строкой-ссылкой,
      // грузить его повторно не нужно. В modules/players проверка была, здесь — нет.
      if (photo?.[0] instanceof File) {
        await playersResource.uploadPhoto(photo[0], playerId, tournamentId);
      }

      if (!userId) {
        await playersRepository.addTournamentUserToTeam({
          userId: playerId,
          teamId,
        });
      }

      const user = await playersRepository.getUser(playerId);

      dispatch(
        addPlayerToTeam({
          teamId,
          // Поля перечислены явно: у TournamentUser.createdAt тип Date, а у TeamPlayer — строка.
          player: {
            id: user.id,
            userId: user.id,
            isReal: false,
            firstName: user.name,
            middleName: user.middleName,
            lastName: user.surname,
            birthDate: user.birthDate,
            height: user.height,
            weight: user.weight,
            avatar: user.photo?.externalUrl ?? USER_DEFAULT_AVATAR,
            position: PlayerPosition.DEFENDER,
            number: 99,
          },
        }),
      );

      dispatch(invalidateTeam(teamId));

      return user;
    } catch (error) {
      console.error(`${PREFIX}/upsertTournamentPlayerApplication error:`, error);
      let message = error instanceof Error ? error.message : String(error);
      return rejectWithValue(message);
    }
  },
);

export const deleteTournamentPlayerApplication = createAsyncThunk<KickFromTeamInputDto, KickFromTeamInputDto>(
  `${PREFIX}/deleteTournamentPlayerApplication`,
  async (payload, { dispatch }) => {
    try {
      const { userId, teamId } = payload;

      dispatch(removePlayerFromTeam(payload));
      await playersRepository.kickTournamentUserFromTeam({ teamId, userId });
      dispatch(invalidateTeam(teamId));

      return payload;
    } catch (err) {
      console.error(`${PREFIX}/deleteTournamentPlayerApplication error:`, err);
      throw err;
    }
  },
);

export const changePlayerRole = createAsyncThunk<string, ChangeParticipantsRoleDto>(
  `${PREFIX}/changePlayerRole`,
  async (payload, { dispatch }) => {
    try {
      const result = await teamParticipantsResource.changeRole(
        payload.teamId,
        payload.userId,
        payload.role,
        payload.number,
        payload.position,
      );
      dispatch(invalidateTeam(payload.teamId));

      return result;
    } catch (err) {
      console.error(`${PREFIX}/changePlayerRole error:`, err);
      throw err;
    }
  },
);
